/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams } from 'react-router';
import * as Yup from 'yup';

import { Button, Collapse, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack } from '@mui/material';

import { Endpoints, Infer } from 'shared';

import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { Namespaces } from '../../../../i18n';
import { AuthenticationApiService } from '../../../../services/AuthenticationApiService';
import { useFormStyles } from '../../../../styles/form';
import { LoginMode } from '../../../../types/common/LoginMode';
import { useObjectState } from '../../../../utils/useObjectState';
import { createLoginPageStyles } from '../../LoginPage.styles';
import { LoginOptions } from './components/LoginOptions/LoginOptions';
import { SsoLoginFormValues, SsoLoginMode } from './SsoLoginForm.types';

type SsoLoginFormProps = {
  swapToPasswordButton: React.ReactNode;
  handleLoginModeChange: (mode: LoginMode) => void;
};

const validationSchema = Yup.object({
  email: Yup.string().email('invalid_email').required('empty'),
});

type SsoLoginModeState = {
  mode: SsoLoginMode;
  visibleMode: SsoLoginMode | null;
};

export const SsoLoginForm = memo<SsoLoginFormProps>(props => {
  const { swapToPasswordButton, handleLoginModeChange } = props;
  const classes = createLoginPageStyles();
  const formClasses = useFormStyles();

  const { t } = useTranslation();

  const [isFetching, setIsFetching] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [loginOption, setLoginOption] =
    useState<Infer<typeof Endpoints.GetAuthenticationLoginOptions>['Response']['200']>();
  const [ssoLoginModeState, setSsoLoginModeState] = useObjectState<SsoLoginModeState>({
    mode: SsoLoginMode.Form,
    visibleMode: SsoLoginMode.Form,
  });

  const handleSsoModeChange = (nextMode: SsoLoginMode) => {
    setSsoLoginModeState({ visibleMode: null, mode: nextMode });
  };

  useEffect(() => {
    if (loginOption && loginOption?.type !== 'oidc') {
      handleSsoModeChange(SsoLoginMode.Option);
    }

    if (loginOption?.type === 'oidc') {
      const fetchOidcRedirect = async () => {
        try {
          setIsSpinning(true);
          const loginOidc = await AuthenticationApiService.loginOidc({
            organizationId: loginOption.organizationId,
            email: values.email.trim(),
          });
          window.location.href = loginOidc.data.payload.redirect.href;
        } catch (e) {
          console.error(e);
        } finally {
          setIsSpinning(false);
        }
      };

      void fetchOidcRedirect();
    }
  }, [loginOption]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExited = () => {
    setSsoLoginModeState({ visibleMode: ssoLoginModeState.mode });
  };

  const submit = async (data: SsoLoginFormValues) => {
    const email = data.email.trim();
    try {
      setIsFetching(true);
      const loginOption = await AuthenticationApiService.loginOptions({ email });
      setLoginOption(loginOption.data.payload);
    } catch (e) {
      console.error(e);
    } finally {
      setIsFetching(false);
    }
  };

  const { handleSubmit, values, handleChange, touched, errors } = useFormik<SsoLoginFormValues>({
    initialValues: { email: '' },
    validationSchema,
    onSubmit: submit,
    validateOnChange: true,
    validateOnMount: true,
  });

  const params = createSearchParams({ email: values.email });

  const hasError = (name: keyof SsoLoginFormValues) => touched[name] && !!errors[name];

  const getError = (name: keyof SsoLoginFormValues): string | undefined => {
    const error = errors[name];
    return error ? t(`${Namespaces.Login}:validation.${name}.${error}`) : undefined;
  };

  const onEdit = () => {
    handleSsoModeChange(SsoLoginMode.Form);
  };

  return (
    <div className={classes.form}>
      <HUSpin spinning={isSpinning}>
        <form className={classes.form} onSubmit={handleSubmit}>
          <FormControl
            variant="outlined"
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('email')}
          >
            <div className={classes.formGroup}>
              <InputLabel htmlFor="email">{t(`${Namespaces.Entities}:user.email.label`)}</InputLabel>
              <OutlinedInput
                id="email"
                name="email"
                label={t(`${Namespaces.Entities}:user.email.label`)}
                error={hasError('email')}
                fullWidth
                value={values.email}
                onChange={handleChange}
                autoComplete="username"
                disabled={isFetching}
                data-cy="sso-login-form-email-input"
                readOnly={ssoLoginModeState.visibleMode === SsoLoginMode.Option}
                endAdornment={
                  ssoLoginModeState.visibleMode === SsoLoginMode.Option && (
                    <Button size="small" onClick={onEdit}>
                      {t('common:form.edit')}
                    </Button>
                  )
                }
              />
              {hasError('email') && (
                <FormHelperText data-cy="sso-login-form-email-error">{getError('email')}</FormHelperText>
              )}
            </div>
          </FormControl>
          <Collapse in={ssoLoginModeState.visibleMode === SsoLoginMode.Form} onExited={handleExited}>
            <Button type="submit" variant="contained" fullWidth disabled={isFetching} data-cy="sso-login-form-next">
              {t(`${Namespaces.Common}:stepper.next`)}
            </Button>
          </Collapse>
        </form>
      </HUSpin>
      <Collapse in={ssoLoginModeState.visibleMode === SsoLoginMode.Option} onExited={handleExited}>
        <div className={classes.loginOption}>
          <LoginOptions
            params={params.toString()}
            option={loginOption}
            email={values.email.trim()}
            handleLoginModeChange={handleLoginModeChange}
          />
        </div>
      </Collapse>
      <Stack spacing={1} sx={{ marginTop: theme => theme.spacing(2) }}>
        {swapToPasswordButton}
      </Stack>
    </div>
  );
});
